/**
 * User Management Page
 * Admin page for managing Echo users and roles
 */

import React, { useCallback } from 'react';
import { Card, Stack, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { UserManagement } from '../components/echo/UserManagement';
import { useEchoUsers, useEchoUserMutations, useEchoMatters } from '../api/echo';
import { useEchoUser } from '../hooks/useEchoUser';

/**
 * Admin page for managing Echo users
 */
export function UserManagementPage() {
  const { theme } = useTheme();
  const { user: currentUser } = useEchoUser();
  const { data: users, loading: isLoading, refetch } = useEchoUsers();
  const { data: allMatters } = useEchoMatters();
  const { updateEchoUser, deleteEchoUser } = useEchoUserMutations();

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  const handlePromoteToAdmin = useCallback(async (userId: number) => {
    await updateEchoUser(userId, { role: 'admin' });
    refetch();
  }, [updateEchoUser, refetch]);

  const handleDemoteToUser = useCallback(async (userId: number) => {
    await updateEchoUser(userId, { role: 'standard' });
    refetch();
  }, [updateEchoUser, refetch]);

  const handleDeleteUser = useCallback(async (userId: number) => {
    await deleteEchoUser(userId);
    refetch();
  }, [deleteEchoUser, refetch]);

  return (
    <Stack gap="lg">
      {/* Page Header */}
      <div>
        <h1 style={{ margin: 0, ...textStyles.h3, color: textColor }}>
          User Management
        </h1>
        <p style={{ margin: 0, marginTop: spacing.xs, ...textStyles.body, color: mutedColor }}>
          Manage Echo users and their roles
        </p>
      </div>

      {/* User Stats */}
      {users && users.length > 0 && (
        <Card variant="outlined">
          <Card.Body>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: spacing.md,
              }}
            >
              <div>
                <div style={{ fontSize: fontSize['2xl'], fontWeight: textStyles.h1.fontWeight, color: textColor }}>
                  {users.length}
                </div>
                <div style={{ fontSize: fontSize.sm, color: mutedColor }}>Total Users</div>
              </div>
              <div>
                <div style={{ fontSize: fontSize['2xl'], fontWeight: textStyles.h1.fontWeight, color: theme.colors.semantic.info.default }}>
                  {users.filter((u) => u.role === 'admin').length}
                </div>
                <div style={{ fontSize: fontSize.sm, color: mutedColor }}>Admins</div>
              </div>
              <div>
                <div style={{ fontSize: fontSize['2xl'], fontWeight: textStyles.h1.fontWeight, color: theme.colors.semantic.success.default }}>
                  {users.filter((u) => u.role === 'standard').length}
                </div>
                <div style={{ fontSize: fontSize.sm, color: mutedColor }}>Standard Users</div>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* User Management Table */}
      <UserManagement
        users={users || []}
        currentUserEmail={currentUser?.email || ''}
        allMatters={allMatters || []}
        isLoading={isLoading}
        onPromoteToAdmin={handlePromoteToAdmin}
        onDemoteToUser={handleDemoteToUser}
        onDeleteUser={handleDeleteUser}
      />

      {/* Help Section */}
      <Card variant="outlined">
        <Card.Body>
          <Stack gap="sm">
            <h3 style={{ margin: 0, ...textStyles.label, color: textColor }}>
              About User Roles
            </h3>
            <ul
              style={{
                margin: 0,
                paddingLeft: spacing.lg,
                color: mutedColor,
                fontSize: fontSize.sm,
              }}
            >
              <li>
                <strong>Admin:</strong> Can manage matters, users, view billing, and access all workflow history
              </li>
              <li>
                <strong>User:</strong> Can create workflows, upload files, and view results only for their assigned matters
              </li>
              <li>Users are automatically created when they first sign in</li>
              <li>Deleted users will be re-created on their next sign-in</li>
            </ul>
          </Stack>
        </Card.Body>
      </Card>
    </Stack>
  );
}

export default UserManagementPage;
